import { useTranslation } from "react-i18next";
import { useDecision } from "@optimizely/react-sdk"
import {
  ColorPicker,
  Input,
  RadioGroup,
  RichEditor,
  RowInfo,
  FormAutocomplete,
  NewImageUpload,
  HelpModal,
} from "../common";
import { Col } from "../App.styles";
import {
  BoldFontModal,
  ChallengesEnabledModal,
  ChallengesVisibilityModal,
  ColorModal,
  CustomEnabledModal,
  CustomURLModal,
  CustomVisibilityModal,
  FontSizeModal,
  HelpEnabledModal,
  HelpVisibilityModal,
  JoinEnabledModal,
  JoinVisibilityModal,
  LeaderboardEnabledModal,
  LeaderboardVisibilityModal,
  LogoModal,
  NotificationsEnabledModal,
  NotificationsVisibilityModal,
  ReelsEnabledModal,
  ReelsVisibilityModal,
  RegularFontModal,
  RewardsEnabledModal,
  RewardsVisibilityModal,
  StreamEnabledModal,
  StreamVisibilityModal,
  SubtitleModal,
  TitleModal,
  TourModal,
  ThemeModal,
} from "./modals";
import { useDefaultHelpTextQuery } from "./Branding.state";
import BrandingSortMenu from "./BrandingSortMenu";
import WatermarkModal from "./modals/WatermarkModal";
import MenuOrderModal from "./modals/MenuOrderModal";
import NpcsEnabledModal from "./modals/NpcsEnabledModal";
import CharactersVisibilityModal from "./modals/CharactersVisibilityModal";

export const themes = [{
  id: 0,
  name: 'Light'
}, {
  id: 1,
  name: 'Dark'
}]

const BrandingForm = ({ useQuery }) => {
  const { t } = useTranslation();
  const defaultText = useDefaultHelpTextQuery();
  const [decisionNpcInBranding] = useDecision('npc-in-branding') 

  return (
    <>
      <RowInfo help={<ColorModal type="top_nav_background_color" />}>
        <ColorPicker
            defaultValue="#3EC5EF"
            label={t("Top Navigation Background Color")}
            name="top_nav_background_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="top_nav_txt_color" />}>
        <ColorPicker
            defaultValue="#FFFFFF"
            label={t("Top Navigation Text Color")}
            name="top_nav_txt_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="top_nav_tint_color" />}>
        <ColorPicker
            defaultValue="#FFFFFF"
            label={t("Top Navigation Tint Color")}
            name="top_nav_tint_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="body_high_color" />}>
        <ColorPicker
            defaultValue="#000000"
            label={t("Body Highlight Color")}
            name="body_high_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="body_txt_color" />}>
        <ColorPicker
            defaultValue="#000000"
            label={t("Body Text Color")}
            name="body_txt_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="body_background_color" />}>
        <ColorPicker
            defaultValue="#3478b9"
            label={t("Body Background Color (Android only)")}
            name="body_background_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ColorModal type="body_tint_color" />}>
        <ColorPicker
            defaultValue="#FD952F"
            label={t("Body Tint Color")}
            name="body_tint_color"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<FontSizeModal />}>
        <FormAutocomplete
            defaultText={16}
            desc={t("Select a font size for the players app.")}
            label={t("Body Font Size (Android and Web)")}
            name="font_size"
            useQuery={useQuery}
            options={[
                { id: 8, name: '8' },
                { id: 10, name: '10' },
                { id: 12, name: '12' },
                { id: 16, name: '16' },
                { id: 20, name: '20' },
            ]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<RegularFontModal />}>
        <FormAutocomplete
            defaultText="Default"
            desc={t("Select a font for the players app.")}
            label={t("Regular Font (Android and Web)")}
            name="font_regular"
            useQuery={useQuery}
            options={[
                { id: "Lato-Regular", name: 'Lato' },
                { id: "OpenSans-Regular", name: 'OpenSans' },
                { id: "Caveat-Regular", name: 'Caveat' },
            ]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<BoldFontModal />}>
        <FormAutocomplete
            defaultText="Default"
            desc={t(`Select a font for the players app.`)}
            label={t(`Bold Font (Android and Web)`)}
            name="font_bold"
            useQuery={useQuery}
            options={[
                { id: "Lato-Bold", name: 'Lato' },
                { id: "OpenSans-Bold", name: 'OpenSans' },
                { id: "Caveat-Bold", name: 'Caveat' },
            ]}
            size="small"
        />
      </RowInfo>
      <RowInfo help={<LogoModal />}>
        <NewImageUpload
            label={t(`Logo`)}
            name="logo"
            useQuery={useQuery}
            width={150}
          />
      </RowInfo>
      <RowInfo help={<ChallengesEnabledModal />}>
        <NewImageUpload
            label={t(`Challenges`)}
            name="challengesenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Challenges Title`)}
            name="challenges_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Challenges Subtitle`)}
            name="challenges_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ChallengesVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Challenges Visibility`)}
            name="challenges_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<StreamEnabledModal />}>
        <NewImageUpload
            label={t(`Stream`)}
            name="streamenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Stream Title`)}
            name="stream_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Stream Subtitle`)}
            name="stream_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<StreamVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Stream Visibility`)}
            name="stream_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<LeaderboardEnabledModal />}>
        <NewImageUpload
            label={t(`Leaderboard`)}
            name="leaderboardenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Leaderboard Title`)}
            name="leaderboard_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Leaderboard Subtitle`)}
            name="leaderboard_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<LeaderboardVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Leaderboard Visibility`)}
            name="leaderboard_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      {decisionNpcInBranding?.enabled && (
        <>
          <RowInfo help={<NpcsEnabledModal />}>
            <NewImageUpload
                label={t(`Character`)}
                name="npcsenabled"
                type="icon"
                useQuery={useQuery}
              />
          </RowInfo>
          <RowInfo help={<TitleModal />}>
            <Input
                help={t(`The headline on the main menu.`)}
                label={t(`Character Title`)}
                name="npcs_title"
                maxLength={30}
                useQuery={useQuery}
              />
          </RowInfo>
          <RowInfo help={<SubtitleModal />}>
            <Input
                help={t(`The subheading on the main menu.`)}
                label={t(`Characters Subtitle`)}
                name="npcs_subtitle"
                maxLength={30}
                useQuery={useQuery}
              />
          </RowInfo>
          <RowInfo help={<CharactersVisibilityModal />}>
            <RadioGroup
              defaultValue={true}
              label={t(`Characters Visibility`)}
              name="npcs_visible"
              options={[
                { value: false, label: t(`Hidden`) },
                { value: true, label: t(`Visible`) },
              ]}
                useQuery={useQuery}
            />
          </RowInfo>
        </>
      )}
      <RowInfo help={<ReelsEnabledModal />}>
        <NewImageUpload
            label={t(`Reels`)}
            name="reelsenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Reels Title`)}
            name="reels_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Reels Subtitle`)}
            name="reels_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ReelsVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Reels Visibility`)}
            name="reels_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<RewardsEnabledModal />}>
        <NewImageUpload
            label={t(`Rewards`)}
            name="rewardsenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Rewards Title`)}
            name="rewards_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Rewards Subtitle`)}
            name="rewards_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<RewardsVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Rewards Visibility`)}
            name="rewards_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<JoinEnabledModal />}>
        <NewImageUpload
            label={t(`Join`)}
            name="joinenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Join Title`)}
            name="join_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Join Subtitle`)}
            name="join_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<JoinVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Join Visibility`)}
            name="join_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<NotificationsEnabledModal />}>
        <NewImageUpload
            label={t(`Chat`)}
            model="games"
            name="notificationsenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Chat Title`)}
            name="notifications_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Chat Subtitle`)}
            name="notifications_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<NotificationsVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Chat Visibility`)}
            name="notifications_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<HelpEnabledModal />}>
        <NewImageUpload
            label={t(`Help`)}
            name="helpenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Help Title`)}
            name="help_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Help Subtitle`)}
            name="help_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<HelpVisibilityModal />}>
        <RadioGroup
            defaultValue={true}
            label={t(`Help Visibility`)}
            name="help_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={(
        <HelpModal title={t(`Help`)}>
            <p>{t(`Specify custom help to be shown to players from the main menu.`)}</p>
        </HelpModal>
      )}>
        <Col size="12">
          <RichEditor
            defaultValue={defaultText}
            label={t(`Help`)}
            name="help"
            useQuery={useQuery}
          />
        </Col>
      </RowInfo>
      <RowInfo help={(
        <HelpModal title={t(`Terms of Use`)}>
            <p>{t(`Specify custom terms of use that the user must agree to as they join the game.`)}</p>
        </HelpModal>
      )}>
        <Col size="12">
          <RichEditor
            label={t(`Terms of Use`)}
            name="terms"
            useQuery={useQuery}
          />
        </Col>
      </RowInfo>
      <RowInfo help={<CustomEnabledModal />}>
        <NewImageUpload
            label={t(`Custom`)}
            name="customenabled"
            type="icon"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TitleModal />}>
        <Input
            help={t(`The headline on the main menu.`)}
            label={t(`Custom Title`)}
            name="custom_title"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<SubtitleModal />}>
        <Input
            help={t(`The subheading on the main menu.`)}
            label={t(`Custom Subtitle`)}
            name="custom_subtitle"
            maxLength={30}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<CustomURLModal />}>
        <Input
            help={t(`The url to load for this custom menu item.`)}
            label={t(`Custom URL`)}
            name="custom_url"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<CustomVisibilityModal />}>
        <RadioGroup
            defaultValue={false}
            label={t(`Custom Visibility`)}
            name="custom_visible"
            options={[
              { value: false, label: t(`Hidden`) },
              { value: true, label: t(`Visible`) },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TourModal />}>
        <NewImageUpload
            label={t(`Tour Image 1`)}
            name="tour_1"
            type="tour"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TourModal />}>
        <NewImageUpload
            label={t(`Tour Image 2`)}
            name="tour_2"
            type="tour"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TourModal />}>
        <NewImageUpload
            label={t(`Tour Image 3`)}
            name="tour_3"
            type="tour"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<TourModal />}>
        <NewImageUpload
            label={t(`Tour Image 4`)}
            name="tour_4"
            type="tour"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<ThemeModal />}>
        <FormAutocomplete
          label={t(`Theme`)}
          name="theme"
          useQuery={useQuery}
          options={themes}
          size="small"
        />
      </RowInfo>
      <RowInfo help={<MenuOrderModal />}>
        <Col size="10">
          <BrandingSortMenu useQuery={useQuery} />
        </Col>
      </RowInfo>
      <RowInfo help={<WatermarkModal />}>
        <NewImageUpload
            label={t(`Watermark`)}
            name="watermark"
            type="watermark"
            useQuery={useQuery}
          />
      </RowInfo>
    </>
  );
};

export default BrandingForm;
